/**
 * Storage quota and persistence (D-033).
 *
 * Every Storage API entry point is feature-detected. Persistence is REQUESTED,
 * never assumed: a granted request materially reduces eviction risk but does
 * not eliminate it, and a declined request must not degrade any behaviour.
 *
 * Nothing here softens the user-facing warning that local browser storage is
 * not guaranteed permanent.
 */

export type PersistenceState = 'granted' | 'denied' | 'unsupported'

export interface StorageEstimateResult {
  readonly supported: boolean
  readonly usageBytes?: number
  readonly quotaBytes?: number
  readonly availableBytes?: number
}

export function isStorageApiAvailable(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.storage !== 'undefined'
}

export function isPersistApiAvailable(): boolean {
  return isStorageApiAvailable() && typeof navigator.storage.persist === 'function'
}

export function isEstimateApiAvailable(): boolean {
  return isStorageApiAvailable() && typeof navigator.storage.estimate === 'function'
}

/**
 * Ask the browser to make this origin's storage persistent.
 *
 * Safari may decline, and that is a normal outcome rather than an error.
 */
export async function requestPersistence(): Promise<PersistenceState> {
  if (!isPersistApiAvailable()) return 'unsupported'
  try {
    return (await navigator.storage.persist()) ? 'granted' : 'denied'
  } catch {
    return 'unsupported'
  }
}

/** Whether persistence has already been granted, without requesting it again. */
export async function isPersisted(): Promise<PersistenceState> {
  if (!isStorageApiAvailable() || typeof navigator.storage.persisted !== 'function') {
    return 'unsupported'
  }
  try {
    return (await navigator.storage.persisted()) ? 'granted' : 'denied'
  } catch {
    return 'unsupported'
  }
}

export async function estimateStorage(): Promise<StorageEstimateResult> {
  if (!isEstimateApiAvailable()) return { supported: false }
  try {
    const estimate = await navigator.storage.estimate()
    const usageBytes = estimate.usage
    const quotaBytes = estimate.quota

    return {
      supported: true,
      ...(usageBytes === undefined ? {} : { usageBytes }),
      ...(quotaBytes === undefined ? {} : { quotaBytes }),
      ...(usageBytes === undefined || quotaBytes === undefined
        ? {}
        : { availableBytes: Math.max(0, quotaBytes - usageBytes) }),
    }
  } catch {
    return { supported: false }
  }
}

/**
 * Whether an operation of `requiredBytes` plus headroom is likely to fit.
 *
 * Returns `'unknown'` when the estimate API is absent — the caller must then
 * proceed and handle a real `QuotaExceededError`, not assume success.
 */
export async function hasRoomFor(
  requiredBytes: number,
  headroomBytes = 50 * 1024 * 1024,
): Promise<boolean | 'unknown'> {
  const estimate = await estimateStorage()
  if (!estimate.supported || estimate.availableBytes === undefined) return 'unknown'
  return estimate.availableBytes >= requiredBytes + headroomBytes
}
